import type { Chip } from "./ride-journey.ts";
import type { DispatchProcess, DispatchStatus } from "./rider-api.ts";

/**
 * What DSP-04's dispatch process means on a phone, away from how it is fetched.
 *
 * `5 Finding` and `5b No driver` are one record in two states, the same shape as `3 Quote` and
 * `3b Quote expired`: the design draws two screens and the platform publishes one row whose `status`
 * decides which. So the decisions about what that row means live here rather than inside the
 * component, for the reason `ride-journey.ts` gives about itself — a screen that collapsed four
 * reasons into one would throw the reason away at the last step.
 *
 * ## The three facts the design draws, and the field behind each
 *
 * | Drawn                               | From                                                 |
 * | ----------------------------------- | ---------------------------------------------------- |
 * | `finding_candidates · try 1 of 3`   | `status`, `attempt`, `maximumAttempts`                |
 * | `Search time left 1:48`             | `deadlineAt`, counted down against this phone's clock |
 * | `No XL drivers are free right now`  | `status: failed` with `failureReason: no_supply`      |
 * | `We asked every nearby driver, 3 times` | `attempt` on the failed process                   |
 *
 * All five were computed by DSP-04 from the day it was written and published only inside the 202 of
 * its event-ingestion `POST`, behind `dispatch:orchestrate`.
 * [ADR 0031](../../../docs/adr/0031-the-dispatch-process-is-read-under-its-own-scope-and-bounded-by-the-tenant-because-the-record-has-no-subject.md)
 * gave them a read of their own under `dispatch:read`, which is the only reason either screen exists
 * in this build.
 *
 * ## The countdown is this phone's clock, and the verdict is not
 *
 * `deadlineAt` is an instant DSP-04 published; the countdown is this application subtracting it from
 * `Date.now()`. That is exactly the split `priceHeldLabel` carries for MOB-02's expiry, and the same
 * caution applies with more force here: a phone whose clock is two minutes fast would run the
 * countdown to zero while the search is still running, and a phone two minutes slow would still be
 * counting down after the orchestrator has given up.
 *
 * **So the countdown never decides anything.** Which screen a rider sees is decided by `status`,
 * which is DSP-04's. When the two disagree — the countdown has run out and `status` is still
 * `finding_candidates` — the screen says so in words rather than showing a negative number or
 * pretending the search has ended, because *both* are possible: a `deadline.reached` event that has
 * not been ingested yet, or a clock that is wrong. `countdownState` below is that distinction, and it
 * is the whole reason it returns a union rather than a string.
 */

/* --------------------------------------------------------------- what this build chose, and why */

/**
 * How often this screen re-reads DSP-04 while it is open.
 *
 * The design draws a **live** search — a countdown, an attempt number out of a ceiling, and a
 * radar — so a screen that read once and then sat still would show `try 1 of 3` for as long as a
 * rider left it open. Nothing delivers a dispatch transition to this application unasked (RTM-001),
 * so following it means asking again, and `CLAUDE.md` section 2 says in as many words what that is
 * allowed to be: _"A 'check status' action is a read, never a retry of the command."_ DSP-04's `GET`
 * is a read of one row, it mints nothing, and it is not marked idempotent because it has nothing to
 * be idempotent about.
 *
 * Five seconds is **this build's number**, not the platform's: nothing publishes how often a
 * dispatch process may be polled, or how long one of its attempts takes. It is chosen as about three
 * reads per attempt on the kind of search the design draws — a deadline under two minutes across
 * three attempts — which is often enough that the attempt number is not visibly wrong and rare
 * enough that a rider waiting ninety seconds makes eighteen requests rather than ninety. It is named
 * here with that reasoning rather than written at the call site, the same treatment
 * `rider-api.ts` gives `RATING_RULE_VERSION` and `CANCELLED_BY_RIDER`, and it is recorded as a
 * design question.
 */
export const SEARCH_REREAD_INTERVAL_MS = 5_000;

/**
 * How long an answer may be held before this screen stops presenting it as current.
 *
 * Three missed re-reads. Below it the screen draws what it last read; above it the screen still
 * draws it — throwing away the only answer there is would leave a rider staring at nothing — but
 * says how old it is and stops the countdown reading as live.
 *
 * Also this build's number, and for a harder reason than the interval above: DSP-04 publishes
 * `updatedAt`, which is when the **orchestrator** last moved the row, and no freshness window at
 * all. So there is nothing to compare against except the instant this application last got an
 * answer. R-23 is the recorded incident of a staleness flag being published and not consulted; this
 * is the opposite shape of problem — no flag is published, so the screen measures what it can and
 * says which clock it measured.
 */
export const SEARCH_STALE_AFTER_MS = 3 * SEARCH_REREAD_INTERVAL_MS;

/* ------------------------------------------------------------------ the state the row is in */

/** Whether DSP-04's five statuses mean the search is still running, has ended, or has given up. */
export type SearchPhase = "searching" | "assigned" | "exhausted";

/**
 * Which of the three the status is.
 *
 * `finding_candidates`, `offering` and `assigning` are all the search still going: the design draws
 * one screen for them and writes the status word itself into the chip, so all three are `searching`
 * and the chip says which. `completed` means DSP-04 bound a driver, which is `6 Driver coming`'s
 * subject rather than this screen's. `failed` is `5b No driver`.
 *
 * Total over the union rather than defaulted, so a sixth status added to the contract fails the
 * build here instead of being drawn as a search that is still running.
 */
export function searchPhase(status: DispatchStatus): SearchPhase {
  switch (status) {
    case "finding_candidates":
    case "offering":
    case "assigning":
      return "searching";
    case "completed":
      return "assigned";
    case "failed":
      return "exhausted";
  }
}

/**
 * The design's chip, which carries the platform's own status word.
 *
 * `finding_candidates · try 1 of 3` is `status`, then `attempt` out of `maximumAttempts`, and the
 * design writes DSP-04's enumerated value verbatim — the same thing `stageChip` does with EXP-002's
 * `stage`, and the reason neither is translated. `offering` and `assigning` therefore print
 * themselves, which is the design's rule applied to the two statuses its sample does not happen to
 * show.
 *
 * `5b No driver`'s chip is the design's own `failed` / `No driver found`, which is **not** the
 * status word: the design names the outcome a rider cares about rather than the saga's. Copied as
 * written.
 *
 * `completed` takes `done` / `completed`, which is `stageChip`'s own pair for the same fact, so a
 * rider who sees it for the half-second before the screen moves on sees the pill they will see on
 * the next screen.
 */
export function searchChip(process: DispatchProcess): Chip {
  switch (searchPhase(process.status)) {
    case "searching":
      return {
        class: "active",
        label: `${process.status} · try ${String(process.attempt)} of ${String(process.maximumAttempts)}`,
      };
    case "assigned":
      return { class: "done", label: "completed" };
    case "exhausted":
      return { class: "failed", label: "No driver found" };
  }
}

/* ------------------------------------------------------------------------------- the countdown */

/**
 * `Search time left 1:48`, or the reason there is no number.
 *
 * Three answers rather than two, and the third is the one that earns the union:
 *
 *   - **`running`** with `m:ss` — the deadline is ahead of this phone's clock.
 *   - **`passed`** — the deadline is behind it and DSP-04 still says the search is running. Not
 *     `0:00`, and not a negative number: the two things that could be true are a `deadline.reached`
 *     event DSP-04 has not ingested yet and a clock on this phone that is wrong, and a rider is told
 *     which fact is missing instead of being shown a number that settles it either way.
 *   - **`unreadable`** — `deadlineAt` did not parse. DSP-04 types it as a plain string with no
 *     `format: date-time`, so this is reachable from the contract rather than only from a fault, and
 *     `Number.isFinite` is the check rather than a `try`.
 *
 * Seconds are floored and the minutes are not padded, which is what `priceHeldLabel` does for the
 * same reason: the design writes `1:48` and `4:12`, never `01:48`.
 */
export type CountdownState =
  | { readonly kind: "running"; readonly label: string; readonly remainingMs: number }
  | { readonly kind: "passed"; readonly byMs: number }
  | { readonly kind: "unreadable" };

export function countdownState(deadlineAt: string, now: number): CountdownState {
  const remaining = Date.parse(deadlineAt) - now;
  if (!Number.isFinite(remaining)) return { kind: "unreadable" };
  if (remaining <= 0) return { kind: "passed", byMs: -remaining };
  const seconds = Math.floor(remaining / 1000);
  return {
    kind: "running",
    label: `${String(Math.floor(seconds / 60))}:${String(seconds % 60).padStart(2, "0")}`,
    remainingMs: remaining,
  };
}

/** What goes in the design's `Search time left` row, in whichever of the three states it is. */
export function searchTimeLeft(countdown: CountdownState): string {
  switch (countdown.kind) {
    case "running":
      return countdown.label;
    case "passed":
      return "none left";
    case "unreadable":
      return "not readable";
  }
}

/* ------------------------------------------------------------------------- how old the answer is */

/**
 * Whether what this screen is drawing may still be presented as current, and how old it is.
 *
 * Measured against the instant this application last **got** an answer, not against DSP-04's
 * `updatedAt`. The two are different clocks and only one of them answers the question a rider has:
 * `updatedAt` is when the orchestrator last moved the row, so a search sitting on attempt 2 for
 * forty seconds has an `updatedAt` forty seconds old and an answer that is perfectly current.
 */
export interface Freshness {
  readonly stale: boolean;
  readonly ageMs: number;
  /** `12s ago`, for the words a stale screen says. */
  readonly label: string;
}

export function freshnessOf(observedAt: number, now: number): Freshness {
  const ageMs = Math.max(0, now - observedAt);
  return { stale: ageMs > SEARCH_STALE_AFTER_MS, ageMs, label: agoLabel(ageMs) };
}

/** `12s ago`, `3 min ago`, `2 h ago` — the same scale `positionAgeLabel` uses, for the same reason. */
export function agoLabel(ageMs: number): string {
  const seconds = Math.round(ageMs / 1000);
  if (seconds < 60) return `${String(seconds)}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${String(minutes)} min ago`;
  return `${String(Math.round(minutes / 60))} h ago`;
}

/* -------------------------------------------------------------------------- why the search ended */

/**
 * `5b No driver`'s heading and sub-line, and whether they are the design's words or this build's.
 *
 * The design draws **one** exhausted search: _"No XL drivers are free right now"_ over _"We asked
 * every nearby driver, 3 times. You haven't been charged."_ That is `failureReason: "no_supply"`,
 * and it is the one of DSP-04's three reasons those two sentences are true of.
 *
 * The other two are reachable and are not the same news:
 *
 *   - **`deadline_exceeded`** — the search ran out of *time* rather than out of drivers, which
 *     DSP-04 reaches on a `deadline.reached` event or on any event arriving past the deadline. A
 *     driver may well have been free; nobody took the job in the window. Telling a rider that no
 *     driver is free would be wrong about the world, and offering a cheaper class as the remedy
 *     would be wrong about the fix.
 *   - **`dispatch_failed`** — `reconcileTerminalAssignment`'s fallback, which is the orchestrator
 *     saying it could not finish rather than saying what it found.
 *
 * And `failureReason: null` on a `failed` process, which the contract permits and the orchestrator
 * never writes. Drawn as its own sentence rather than folded into one of the three, because a
 * failed dispatch with no reason recorded is a fault worth being visible.
 *
 * `designed` is false on all but the first, and the component puts it in `PhoneShell`'s `flag` so
 * the design team can replace the wording without hunting for it.
 */
export interface Exhaustion {
  readonly title: string;
  readonly sub: string;
  /** True only for the one case the design draws, which is `no_supply`. */
  readonly designed: boolean;
  /** Whether offering a cheaper class is the right remedy for *this* reason. */
  readonly offerAnotherClass: boolean;
}

export function exhaustion(input: {
  readonly failureReason: string | null;
  /** How many times the search asked, which is `attempt` on the failed process. */
  readonly attempts: number;
  /** MOB-01's display name for the class the rider booked, or null when it could not be read. */
  readonly className: string | null;
}): Exhaustion {
  const { failureReason, attempts, className } = input;

  if (failureReason === "no_supply") {
    return {
      /*
       * The design's own sentence, with MOB-01's display name in it.
       *
       * `No XL drivers are free right now` — `XL` is the class the rider booked, joined through
       * MOB-01's listing from the `quote.product.id` MOB-03 publishes on the request. When that join
       * fails the word is dropped rather than replaced: the sentence is still true without it, and
       * printing an identifier or guessing a name where a class belongs is the fault EXP-002 states
       * its own rule about.
       */
      title:
        className === null
          ? "No drivers are free right now"
          : `No ${className} drivers are free right now`,
      sub: `We asked every nearby driver, ${String(attempts)} times. You haven't been charged.`,
      designed: className !== null,
      offerAnotherClass: true,
    };
  }

  if (failureReason === "deadline_exceeded") {
    return {
      title: "The search ran out of time",
      sub: `We asked for ${String(attempts)} ${attempts === 1 ? "round" : "rounds"} and the search window closed before anybody took it. You haven't been charged.`,
      designed: false,
      /*
       * A cheaper class is not the remedy for a window that closed.
       *
       * The design offers `Try Standard instead` because no XL driver was free. Nothing says the same
       * about a search that timed out — a driver may have been available the whole time — so the row
       * is still drawn, because nothing may be removed, and the action says this instead of implying
       * that a cheaper class is what was wrong.
       */
      offerAnotherClass: false,
    };
  }

  if (failureReason === null) {
    return {
      title: "The search ended and we can't say why",
      sub: "The dispatch for this ride is recorded as failed with no reason against it. You haven't been charged.",
      designed: false,
      offerAnotherClass: false,
    };
  }

  return {
    title: "We couldn't finish the search for your driver",
    sub: `The dispatch for this ride ended as ${failureReason}. You haven't been charged.`,
    designed: false,
    offerAnotherClass: false,
  };
}

/**
 * `Asking drivers near Westlands.` — the design's sub-line on `5 Finding`, and its two fallbacks.
 *
 * The place is the pickup, and there are two published forms of it. GEO-02 answers
 * `address.locality` — `Westlands`, which is the design's own word — and `address.formatted` —
 * `Westlands, Sarit Centre`, which is the same place said longer. Both are GEO-02's; neither is
 * this application's. So the locality is preferred, the formatted address is the second choice, and
 * only when GEO-02 answered nothing at all does the sentence lose its first half.
 *
 * `locality` is optional on GEO-02's answer rather than required, which is why there is a second
 * choice at all. The design is silent about what to draw when the pickup cannot be named, so this is
 * a recorded assumption rather than a blocking question.
 */
export function askingNear(place: { locality?: string; formatted?: string } | null): {
  readonly sub: string;
  readonly designed: boolean;
} {
  const named = place?.locality ?? place?.formatted;
  if (named === undefined || named.length === 0) {
    return { sub: "Usually under a minute.", designed: false };
  }
  return {
    sub: `Asking drivers near ${named}. Usually under a minute.`,
    designed: place?.locality !== undefined && place.locality.length > 0,
  };
}

/**
 * The key this build sends when a rider cancels from the search, derived rather than minted.
 *
 * MOB-03's cancel requires an `Idempotency-Key`, and a fresh one per press would make two taps two
 * cancellations of a request that can only be cancelled once. Keyed on the request **and the version
 * being cancelled**, so a retry of the same press is the same command and a cancel of a row that has
 * moved on is a different one — which is what makes Document 06's refusal of a reused key with
 * changed terms a guard here rather than an obstacle.
 */
export function cancelKey(rideRequestId: string, expectedVersion: number): string {
  return `ride-cancel:${rideRequestId}:v${String(expectedVersion)}`;
}
